import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/cn";
import { site } from "@/content/site";
import mark from "@/assets/brand/logo-mark.png";

/**
 * The firm's lockup, set the way the brand collateral sets it: the M&A mark,
 * a hairline, then the name over the service line — "Mutiu Adejumobi" in ink,
 * "&" in the mark's blue, "Associates Ltd" in its green.
 *
 * Deliberately sans rather than the site's display face. A lockup is fixed
 * artwork that has to survive on a mug, a name plate and a letterhead
 * unchanged; it is not site typography.
 */
export function Wordmark({
  tone = "ink",
  className,
  showDisciplines = true,
}: {
  tone?: "ink" | "paper";
  className?: string;
  showDisciplines?: boolean;
}) {
  const onInk = tone === "ink";

  return (
    <Link
      href="/"
      aria-label={`${site.name} — home`}
      className={cn("group/mark inline-flex items-center gap-3 py-2", className)}
    >
      <Image
        src={mark}
        alt=""
        priority
        sizes="72px"
        className="h-9 w-auto sm:h-12"
      />

      <span
        aria-hidden="true"
        className={cn(
          "h-9 w-px shrink-0 sm:h-12",
          onInk ? "bg-ink-900/15" : "bg-paper/25",
        )}
      />

      <span className="flex flex-col gap-[0.2rem]">
        <span className="text-[0.875rem] leading-[1.18] font-semibold tracking-[-0.01em] sm:text-[1.0625rem]">
          <span className={onInk ? "text-ink-900" : "text-paper"}>
            Mutiu Adejumobi
          </span>
          <br />
          <span className={onInk ? "text-sky-600" : "text-sky-400"}>
            &amp;
          </span>{" "}
          <span className={onInk ? "text-leaf-600" : "text-leaf-500"}>
            Associates Ltd
          </span>
        </span>

        {showDisciplines ? (
          <span
            className={cn(
              "hidden items-center gap-1.5 text-[0.5rem] leading-none font-medium tracking-[0.2em] uppercase sm:flex",
              onInk ? "text-ink-700" : "text-ink-200",
            )}
          >
            {site.lockupLine.map((word, index) => (
              <span key={word} className="contents">
                {index > 0 ? (
                  <span
                    aria-hidden="true"
                    className={
                      // The separators alternate the two brand colours.
                      index % 2 ? "text-sky-500" : "text-leaf-500"
                    }
                  >
                    |
                  </span>
                ) : null}
                <span>{word}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </Link>
  );
}
